import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { COLORS } from "../../constants";
import useSystemHook from "./../../hooks/SystemHook";
import LoadableImage from "../basic/LoadableImage";

const IMAGE_BASE_URL =
  "https://argusweb.azureedge.net/argus-web/merchandising/";

const MerchSection = ({ image, heading, points, shopNowLink }) => {
  const { isMobile } = useSystemHook();

  return (
    <Box
      sx={{
        backgroundColor: COLORS.white,
        border: `solid 1px ${COLORS.border}`,
        borderRadius: 1,
        overflow: "hidden",
        mb: 4,
      }}
    >
      {/* Image */}
      <LoadableImage
        src={image}
        alt={heading}
        style={{
          width: "100%",
          height: "auto",
          display: "block",
          padding: "20px",
        }}
        loaderSx={{ height: 240 }}
      />

      {/* Content */}
      <Box sx={{ p: { xs: 3 } }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 500,
            color: COLORS.darkText,
            mb: 1,
            fontSize: isMobile ? "var(--font-size-m)" : "var(--font-size-xl)",
          }}
        >
          {heading}
        </Typography>

        <Box
          sx={{
            width: "60px",
            height: "4px",
            backgroundColor: COLORS.themeRed,
            mb: 3,
          }}
        />

        <Box component="ul" sx={{ pl: 2, mb: 4 }}>
          {points.map((point, idx) => (
            <Typography
              key={idx}
              component="li"
              sx={{
                mb: 1.5,
                color: COLORS.lightText,
                fontSize: isMobile
                  ? "var(--font-size-xxs)"
                  : "var(--font-size-m)",
              }}
            >
              {point}
            </Typography>
          ))}
        </Box>

        <Button
          variant="contained"
          href={shopNowLink}
          sx={{
            backgroundColor: COLORS.themeRed,
            color: COLORS.white,
            fontSize: isMobile ? "var(--font-size-xxs)" : "var(--font-size-m)",
            px: 5,
            py: 1.5,
            fontWeight: 400,
            borderRadius: 0,
            width: "100%",
            "&:hover": { backgroundColor: COLORS.themeRed, opacity: 0.9 },
          }}
        >
          SHOP NOW
        </Button>
      </Box>
    </Box>
  );
};

const MerchContent = ({ selectedCategory }) => {
  const { isMobile } = useSystemHook();

  const banner = (mobileNum, desktopNum) =>
    `https://argusweb.azureedge.net/argus-web/trade-show/banners/Banners-${
      isMobile ? mobileNum : desktopNum
    }.webp`;

  // Helper to get full image URL from API
  const getImageUrl = (filename) => `${IMAGE_BASE_URL}${filename}.png`;

  const getContent = () => {
    switch (selectedCategory) {
      case "M7":
        return {
          title: "M7",
          bannerImage: banner("04", "01"),
          bannerLink: "/products/all?vendor=beta_merchandising",
          sections: [
            {
              image: getImageUrl("m7Accessories"),
              heading: "Automotive Oils & Greases",
              points: ["Trusted Automotive Oils and Greases"],
              shopNowLink: "/products/all?vendor=beta_merchandising",
            },
          ],
        };

      case "FLIGHT":
        return {
          title: "FLIGHT",
          bannerImage: banner("10", "09"),
          bannerLink: "/products/all?vendor=beta_merchandising",
          sections: [
            {
              image: getImageUrl("flightAccessories"),
              heading: "Automotive Cleaning Products",
              points: ["The most effective cleaning products"],
              shopNowLink: "/products/all?vendor=beta_merchandising",
            },
          ],
        };

      case "BETA":
      default:
        return {
          title: "BETA",
          bannerImage: banner("12", "11"),
          bannerLink: "/products/all?vendor=beta_merchandising",
          sections: [
            {
              image: getImageUrl("betaAccessories"),
              heading: "Automotive Products",
              points: [
                "480 Curated automotive fully merchandised and ready to launch.",
                "320 Automotive Essentials (globes, spark plugs, relays, fuses, terminals, and more)",
              ],
              shopNowLink: "/products/all?vendor=beta_merchandising",
            },
            {
              image: getImageUrl("carAccessories"),
              heading: "Automotive Essentials",
              points: ["128 Automotive accessories.", "99% UNIVERSAL"],
              shopNowLink: "/products/all?vendor=beta_merchandising",
            },
          ],
        };
    }
  };

  const content = getContent();

  return (
    <Box>
      {/* Main Title */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: isMobile ? "var(--font-size-m)" : "var(--font-size-xl)",
            display: "inline-block",
            backgroundColor: COLORS.themeYellow,
            color: COLORS.themeBlack,
            px: 3,
            py: 1.5,
            borderRadius: "4px",
            textAlign: "left",
          }}
        >
          {content.title}
        </Typography>
      </Box>

      {/* Banner */}
      <Box sx={{ mb: 6, textAlign: "center" }}>
        <a href={content.bannerLink} style={{ textDecoration: "none" }}>
          <LoadableImage
            src={content.bannerImage}
            alt={`${content.title} Banner`}
            style={{
              width: "100%",
              maxWidth: "1200px",
              height: "auto",
              borderRadius: 4,
            }}
            loaderSx={{ height: 300, maxWidth: 1200, mx: "auto" }}
          />
        </a>
      </Box>

      {/* Sections */}
      {content.sections.map((section, index) => (
        <MerchSection
          key={index}
          image={section.image}
          heading={section.heading}
          points={section.points}
          shopNowLink={section.shopNowLink}
        />
      ))}
    </Box>
  );
};

export default MerchContent;
